import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as usersApi from '../api/usersApi';
import { PaymentBadge, StatusBadge } from '../components/StatusBadge';
import { Card, EmptyState, ErrorBanner, Skeleton } from '../components/ui';
import { UserFormulations } from '../components/UserFormulations';
import { UserJourney } from '../components/SessionTimeline';
import { IconArrowLeft } from '../components/icons';
import { isAwaitingPayment } from '../types/order';
import { UserDetail } from '../types/user';
import { capitalize, formatDate, formatDateTime, formatINR, shortId } from '../utils/format';

function Chip({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'gold' }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
        tone === 'gold' ? 'border-gold/40 bg-gold/10 text-gold' : 'border-edge bg-edge/30 text-sub'
      }`}
    >
      {children}
    </span>
  );
}

export default function UserDetailPage() {
  const { id = '' } = useParams();
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDetail(await usersApi.getUser(id));
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-32" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Link to="/users" className="btn-ghost -ml-3">
          <IconArrowLeft className="h-4 w-4" /> Back to users
        </Link>
        <ErrorBanner message={error || 'User not found'} onRetry={load} />
      </div>
    );
  }

  const { user, profile, saved_addresses, recent_orders } = detail;

  return (
    <div className="space-y-6">
      <Link to="/users" className="btn-ghost -ml-3">
        <IconArrowLeft className="h-4 w-4" /> Back to users
      </Link>

      {/* User summary */}
      <div className="card p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gold/15 text-base font-semibold text-gold">
              {(user.name || user.email).charAt(0).toUpperCase()}
            </div>
            <div>
              <h1 className="text-lg font-semibold">{user.name || '—'}</h1>
              <p className="text-sm text-sub">{user.email}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone={user.role === 'admin' ? 'gold' : 'neutral'}>{capitalize(user.role)}</Chip>
            <Chip>{user.is_verified ? 'Verified' : 'Unverified'}</Chip>
            {user.is_active === false && <Chip>Deactivated</Chip>}
          </div>
        </div>
        <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-edge pt-4 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs uppercase tracking-wide text-sub">Joined</dt>
            <dd className="mt-0.5 font-medium">{formatDate(user.created_at)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-sub">Last login</dt>
            <dd className="mt-0.5 font-medium">{formatDateTime(user.last_login)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-sub">Orders</dt>
            <dd className="mt-0.5">
              <Link to={`/orders?user=${user.id}`} className="font-medium text-gold hover:underline">
                View all orders
              </Link>
            </dd>
          </div>
        </dl>
      </div>

      {/* Saved addresses */}
      <Card title={`Saved addresses (${saved_addresses.length})`}>
        {saved_addresses.length === 0 ? (
          <EmptyState message="No saved addresses" hint="This customer hasn't saved any delivery addresses yet" />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {saved_addresses.map((a) => (
              <div key={a.id} className="rounded-xl border border-edge p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Chip>{a.label}</Chip>
                  {a.is_default && <Chip tone="gold">Default</Chip>}
                </div>
                <p className="text-sm font-medium">{a.full_name}</p>
                <p className="text-sm text-sub">{a.phone}</p>
                <p className="mt-1 text-sm text-sub">
                  {[a.address_line1, a.address_line2].filter(Boolean).join(', ')}
                </p>
                <p className="text-sm text-sub">
                  {a.city}, {a.state} — {a.pincode}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Scent preferences */}
      {profile &&
        (profile.preferred_moods?.length > 0 ||
          profile.preferred_notes?.length > 0 ||
          profile.disliked_notes?.length > 0) && (
          <Card title="Scent preferences">
            <dl className="space-y-3 text-sm">
              {profile.preferred_moods?.length > 0 && (
                <div>
                  <dt className="mb-1.5 text-xs uppercase tracking-wide text-sub">Preferred moods</dt>
                  <dd className="flex flex-wrap gap-1.5">
                    {profile.preferred_moods.map((m) => (
                      <Chip key={m}>{m}</Chip>
                    ))}
                  </dd>
                </div>
              )}
              {profile.preferred_notes?.length > 0 && (
                <div>
                  <dt className="mb-1.5 text-xs uppercase tracking-wide text-sub">Preferred notes</dt>
                  <dd className="flex flex-wrap gap-1.5">
                    {profile.preferred_notes.map((n) => (
                      <Chip key={n}>{n}</Chip>
                    ))}
                  </dd>
                </div>
              )}
              {profile.disliked_notes?.length > 0 && (
                <div>
                  <dt className="mb-1.5 text-xs uppercase tracking-wide text-sub">Disliked notes</dt>
                  <dd className="flex flex-wrap gap-1.5">
                    {profile.disliked_notes.map((n) => (
                      <Chip key={n}>{n}</Chip>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          </Card>
        )}

      {/* Formulations — loads independently of the profile above, so the AI
          service being slow or down never holds up the rest of the page. */}
      <UserFormulations userId={user.id} />

      {/* Recent orders */}
      <Card title="Recent orders" bodyClassName="!p-0">
        {recent_orders.length === 0 ? (
          <EmptyState message="No orders yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-edge">
                  <th className="th">Order</th>
                  <th className="th">Date</th>
                  <th className="th">Amount</th>
                  <th className="th">Status</th>
                  <th className="th">Payment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {recent_orders.map((o) => (
                  <tr key={o.id} className="transition-colors hover:bg-edge/20">
                    <td className="td">
                      <Link to={`/orders/${o.id}`} className="font-medium text-gold hover:underline">
                        {shortId(o.id)}
                      </Link>
                    </td>
                    <td className="td text-sub">{formatDate(o.created_at)}</td>
                    <td className="td font-medium">{formatINR(o.total)}</td>
                    <td className="td">
                      <StatusBadge
                        status={o.status}
                        label={isAwaitingPayment(o) ? 'Awaiting payment' : undefined}
                      />
                    </td>
                    <td className="td">
                      <PaymentBadge status={o.payment_status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/*
        Journey — every browser session this user has had, including the
        anonymous ones that were stitched to them on login. This is where the
        errors somebody hit BEFORE signing in become visible against their
        account.
      */}
      <Card
        title="Journey"
        action={<span className="text-xs text-sub">Sessions &amp; requests · last 30 days</span>}
      >
        <UserJourney userId={user.id} />
      </Card>
    </div>
  );
}
